import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Zap, Download, TrendingUp, TrendingDown, Minus, AlertCircle, Loader2, Eye, BookOpen, Target, Lightbulb, HelpCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RegretOutlook({ outlook = 'mixed', reasoning, label }) {
  const config = {
    lower: { title: 'Lower anticipated regret', color: '#10B981' },
    mixed: { title: 'Mixed anticipated regret', color: '#F59E0B' },
    higher: { title: 'Higher anticipated regret', color: '#EF4444' },
  };
  const value = config[outlook] || config.mixed;
  return (
    <div className="rounded-xl p-5 text-left" style={{ background: `${value.color}12`, border: `1px solid ${value.color}33` }} data-testid={`regret-outlook-${label}`}>
      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: value.color }}>Reflection label</p>
      <p className="text-base font-semibold mb-3" style={{ fontFamily: 'Syne', color: value.color }}>{value.title}</p>
      <p className="text-xs leading-relaxed" style={{ color: '#94A3B8' }}>{reasoning}</p>
    </div>
  );
}

function ScenarioCard({ type, data }) {
  const config = {
    optimistic: { icon: TrendingUp, color: '#10B981', label: 'Optimistic', bgColor: 'rgba(16,185,129,0.1)' },
    realistic: { icon: Minus, color: '#F59E0B', label: 'Realistic', bgColor: 'rgba(245,158,11,0.1)' },
    pessimistic: { icon: TrendingDown, color: '#EF4444', label: 'Pessimistic', bgColor: 'rgba(239,68,68,0.1)' },
  };
  const c = config[type];
  const Icon = c.icon;
  const likelihood = ['less_likely', 'plausible', 'more_likely'].includes(data?.likelihood)
    ? data.likelihood
    : 'plausible';

  return (
    <div className="glass-card rounded-xl p-6" data-testid={`scenario-${type}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: c.bgColor }}>
          <Icon className="w-4 h-4" style={{ color: c.color }} />
        </div>
        <div>
          <h4 className="text-sm font-semibold" style={{ fontFamily: 'Syne', color: c.color }}>{c.label} Scenario</h4>
          <p className="text-xs capitalize" style={{ color: '#64748B' }}>
            {likelihood.replace('_', ' ')} · qualitative estimate
          </p>
        </div>
      </div>
      <div className="text-sm leading-relaxed mb-4 whitespace-pre-wrap" style={{ color: '#D1D5DB', fontFamily: 'Manrope' }}>
        {data?.narrative || 'No narrative available.'}
      </div>
      {data?.likelihood_reason && (
        <p className="text-xs leading-relaxed mb-4" style={{ color: '#64748B' }}>
          Why consider it: {data.likelihood_reason}
        </p>
      )}
      {data?.key_factors?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {data.key_factors.map((f, i) => (
            <span key={i} className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', color: '#94A3B8' }}>{f}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SimulationResultPage() {
  const { decisionId } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [decRes, simRes] = await Promise.all([
        axios.get(`${API}/decisions/${decisionId}`, { headers, withCredentials: true }),
        axios.get(`${API}/simulations/${decisionId}`, { headers, withCredentials: true }).catch(() => null)
      ]);
      setDecision(decRes.data);
      if (simRes) setSimulation(simRes.data);
    } catch (err) {
      toast.error('Failed to load simulation data');
    } finally {
      setLoading(false);
    }
  }, [decisionId, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/simulations/${decisionId}/pdf`, {
        headers: getAuthHeaders(),
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `regret_engine_report_${decisionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#6366F1' }} />
      </div>
    );
  }

  const result = simulation?.result;

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="glass-card rounded-xl p-12 text-center max-w-md" data-testid="no-simulation">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#F59E0B' }} />
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>No Simulation Yet</h2>
          <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>This decision hasn't been simulated yet.</p>
          <Button data-testid="back-to-dashboard-no-sim" onClick={() => navigate('/dashboard')} className="rounded-full px-6" style={{ background: '#6366F1' }}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const optA = result.option_a || {};
  const optB = result.option_b || {};

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      {/* Nav */}
      <nav className="glass-heavy sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="download-pdf" onClick={handleDownloadPDF} disabled={downloading} variant="outline"
              className="rounded-full px-4 py-2 text-xs border-white/10 hover:bg-white/5">
              {downloading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
              Download PDF
            </Button>
            <Button data-testid="back-to-dashboard-btn" onClick={() => navigate('/dashboard')} variant="ghost" className="text-sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Decision Header */}
        <div className="mb-10" data-testid="simulation-header">
          <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: '#6366F1' }}>Simulation Results</p>
          <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Syne' }}>{decision?.title}</h1>
          <p className="text-sm max-w-2xl" style={{ color: '#94A3B8' }}>{decision?.description}</p>
        </div>

        <div className="rounded-xl p-5 mb-8" style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)' }} data-testid="analysis-basis">
          <p className="text-sm font-semibold mb-1" style={{ color: '#93C5FD' }}>Illustrative analysis—not a measured forecast</p>
          <p className="text-xs leading-relaxed" style={{ color: '#94A3B8' }}>
            {result.analysis_basis?.evidence_note || 'These scenarios were generated from your inputs and general model knowledge. No live research or validated outcome dataset was used.'}
          </p>
        </div>

        {/* Anticipated regret comparison */}
        <div className="glass-card rounded-xl p-8 mb-10" data-testid="regret-comparison">
          <h3 className="text-lg font-semibold mb-2 text-center" style={{ fontFamily: 'Syne' }}>Anticipated Regret Reflection</h3>
          <p className="text-xs text-center mb-8" style={{ color: '#64748B' }}>Qualitative prompts based on reversibility, downside and values—not calibrated scores.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-sm font-semibold mb-4" style={{ fontFamily: 'Syne', color: '#F8FAFC' }}>
                {optA.label || 'Option A'}
              </p>
              <RegretOutlook outlook={optA.regret_outlook} reasoning={optA.regret_reasoning} label="a" />
              {(optA.reasoning_lens || optA.reference_class) && (
                <p className="mt-3 text-[11px]" style={{ color: '#475569' }}>Reasoning lens—not evidence: {optA.reasoning_lens || optA.reference_class}</p>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold mb-4" style={{ fontFamily: 'Syne', color: '#F8FAFC' }}>
                {optB.label || 'Option B'}
              </p>
              <RegretOutlook outlook={optB.regret_outlook} reasoning={optB.regret_reasoning} label="b" />
              {(optB.reasoning_lens || optB.reference_class) && (
                <p className="mt-3 text-[11px]" style={{ color: '#475569' }}>Reasoning lens—not evidence: {optB.reasoning_lens || optB.reference_class}</p>
              )}
            </div>
          </div>
        </div>

        {/* Decision lens */}
        {result.comparison && (
          <div className="glass-card rounded-xl p-8 mb-10" data-testid="decision-lens">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
                <Target className="w-5 h-5" style={{ color: '#6366F1' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Syne' }}>Decision Lens</h3>
                <p className="text-xs" style={{ color: '#475569' }}>Conditions to examine—not an instruction about what to choose.</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed mb-5" style={{ color: '#D1D5DB' }}><strong>Core trade-off:</strong> {result.comparison.core_tradeoff}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div className="rounded-lg p-4" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: '#A5B4FC' }}>{optA.label || 'Option A'} fits if…</p>
                <p className="text-sm leading-relaxed" style={{ color: '#D1D5DB' }}>{result.comparison.option_a_fits_if}</p>
              </div>
              <div className="rounded-lg p-4" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: '#A5B4FC' }}>{optB.label || 'Option B'} fits if…</p>
                <p className="text-sm leading-relaxed" style={{ color: '#D1D5DB' }}>{result.comparison.option_b_fits_if}</p>
              </div>
            </div>
            <p className="text-xs capitalize mb-4" style={{ color: '#F59E0B' }}>Uncertainty: {result.comparison.uncertainty || 'high'}</p>
            {result.comparison.what_would_change_analysis?.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-semibold mb-2" style={{ color: '#94A3B8' }}>What could change this analysis</p>
                <ul className="space-y-1">
                  {result.comparison.what_would_change_analysis.map((item, index) => <li key={index} className="text-xs" style={{ color: '#64748B' }}>• {item}</li>)}
                </ul>
              </div>
            )}
            {result.key_question && (
              <div className="mt-5 flex items-start gap-3 pt-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <HelpCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#6366F1' }} />
                <div>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#A5B4FC' }}>Key takeaway</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#D1D5DB' }}>{result.key_question}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Assumptions the model inferred */}
        {result.assumptions?.length > 0 && (
          <div className="glass-card rounded-xl p-8 mb-10" data-testid="assumptions">
            <div className="flex items-center gap-3 mb-4">
              <Lightbulb className="w-5 h-5" style={{ color: '#F59E0B' }} />
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Syne' }}>What We Assumed</h3>
            </div>
            <p className="text-xs mb-4" style={{ color: '#94A3B8' }}>
              You didn't have to spell everything out — here's what we inferred to fill the gaps. If any of these is wrong, re-run with that detail corrected.
            </p>
            <ul className="space-y-2">
              {result.assumptions.map((a, i) => (
                <li key={i} className="text-sm leading-relaxed flex items-start gap-2" style={{ color: '#D1D5DB' }}>
                  <span style={{ color: '#F59E0B' }}>•</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Scenario Narratives */}
        <Tabs defaultValue="option_a" className="mb-10" data-testid="scenario-tabs">
          <TabsList className="w-full rounded-xl p-1 mb-6" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <TabsTrigger value="option_a" className="flex-1 rounded-lg py-3 text-sm data-[state=active]:bg-[#6366F1] data-[state=active]:text-white" data-testid="tab-option-a">
              <BookOpen className="w-4 h-4 mr-2" /> {optA.label || 'Option A'}
            </TabsTrigger>
            <TabsTrigger value="option_b" className="flex-1 rounded-lg py-3 text-sm data-[state=active]:bg-[#6366F1] data-[state=active]:text-white" data-testid="tab-option-b">
              <Eye className="w-4 h-4 mr-2" /> {optB.label || 'Option B'}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="option_a" className="space-y-4" data-testid="option-a-content">
            <ScenarioCard type="optimistic" data={optA.optimistic} />
            <ScenarioCard type="realistic" data={optA.realistic} />
            <ScenarioCard type="pessimistic" data={optA.pessimistic} />
          </TabsContent>
          <TabsContent value="option_b" className="space-y-4" data-testid="option-b-content">
            <ScenarioCard type="optimistic" data={optB.optimistic} />
            <ScenarioCard type="realistic" data={optB.realistic} />
            <ScenarioCard type="pessimistic" data={optB.pessimistic} />
          </TabsContent>
        </Tabs>

        {/* Overall Insight */}
        {result.overall_insight && (
          <div className="glass-card rounded-xl p-8 mb-10" data-testid="overall-insight">
            <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Syne' }}>Overall Insight</h3>
            <p className="text-sm leading-relaxed" style={{ color: '#D1D5DB' }}>{result.overall_insight}</p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="rounded-xl p-6 mb-10" style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)' }} data-testid="disclaimer">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
            <div>
              <p className="text-sm font-semibold mb-1" style={{ color: '#F59E0B' }}>Important Disclaimer</p>
              <p className="text-xs leading-relaxed" style={{ color: '#94A3B8' }}>
                {result.disclaimer || "This is a simulation based on patterns — not your life. You know things no dataset does. The decision remains 100% yours."}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4 justify-center" data-testid="result-actions">
          <Button data-testid="new-simulation-btn" onClick={() => navigate('/decision/new')} className="rounded-full px-8 py-5 text-sm font-semibold"
            style={{ background: '#6366F1', boxShadow: '0 0 15px rgba(99,102,241,0.3)' }}>
            New Simulation
          </Button>
          <Button data-testid="result-download-pdf" onClick={handleDownloadPDF} disabled={downloading} variant="outline"
            className="rounded-full px-8 py-5 text-sm border-white/10 hover:bg-white/5">
            <Download className="w-4 h-4 mr-2" /> Download PDF Report
          </Button>
        </div>
      </div>
    </div>
  );
}
